import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { formatHour12 } from '@/lib/time-format';
import formStyles from '@/components/Form.module.css';
import styles from './TimeWindowRows.module.css';

/**
 * PWA copy of the admin-side `TimeWindowRows` shared component
 * (extracted in Phase 7.2 of the resident-managed peer-to-peer
 * chargers ticket). Same logic, same shape — but uses the PWA's
 * --evi-* CSS variables so it skin-switches with the rest of the
 * driver UI. Cross-app imports between admin and web aren't wired
 * (the PWA build can't reach into apps/admin/src), so we duplicate
 * here and accept the drift; future cleanup lifts to a shared
 * @evi/ui primitive.
 *
 * Single caller in PWA today: MyChargerDetailPage's guest_time_windows
 * editor. Charger callers don't need the extra-column slot, but the
 * generic shape kept for parity with the admin component (and for
 * the inevitable second PWA caller).
 */

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export interface TimeWindowRowsExtraColumn<T> {
  label: string;
  render: (window: T, onRowChange: (next: T) => void, disabled: boolean) => ReactNode;
}

export interface TimeWindowRowsProps<T> {
  windows: T[];
  onChange: (next: T[]) => void;
  getDays: (window: T) => number[];
  setDays: (window: T, days: number[]) => T;
  getStartHour: (window: T) => number;
  setStartHour: (window: T, hour: number) => T;
  getEndHour: (window: T) => number;
  setEndHour: (window: T, hour: number) => T;
  createEmpty: () => T;
  /**
   * Stable React key for a row. Rows have no server-side id — the wire
   * shape is `{start_hour, end_hour, days_of_week}` with
   * `additionalProperties: false`, so an id cannot round-trip. The
   * caller therefore mints one client-side and carries it in its own
   * row state, stripping it before building the request body.
   *
   * Falls back to the array index when omitted. That is safe only while
   * every control in a row is fully derived from props (as they are
   * here) — an index key cannot corrupt rendered values, but it does
   * make React reuse the wrong DOM node across a splice, which loses
   * focus, restarts transitions, and defeats any future uncontrolled
   * input. Callers that can mint an id should.
   */
  getKey?: (window: T, index: number) => string | number;
  extraColumn?: TimeWindowRowsExtraColumn<T>;
  emptyStateText?: string;
  addButtonText?: string;
  startHourMin?: number;
  startHourMax?: number;
  endHourMin?: number;
  endHourMax?: number;
  allowEmpty?: boolean;
  disabled?: boolean;
}

function clampHour(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  if (n < min) return min;
  if (n > max) return max;
  return Math.floor(n);
}

/**
 * Generate the [min..max] inclusive integer range of hour-of-day
 * values that populate a select's options. Wire values stay as
 * 0-23 ints (or 1-24 for end_hour); only the rendered label
 * shifts to 12-hour AM/PM via formatHour12.
 */
function hourOptions(min: number, max: number): number[] {
  const out: number[] = [];
  for (let h = min; h <= max; h += 1) out.push(h);
  return out;
}

export function TimeWindowRows<T>({
  windows,
  onChange,
  getDays,
  setDays,
  getStartHour,
  setStartHour,
  getEndHour,
  setEndHour,
  createEmpty,
  getKey,
  extraColumn,
  emptyStateText,
  addButtonText,
  startHourMin = 0,
  startHourMax = 23,
  endHourMin = 1,
  endHourMax = 24,
  allowEmpty = true,
  disabled = false,
}: TimeWindowRowsProps<T>) {
  const { t } = useTranslation('typeaheads');
  const updateAt = (idx: number, mutator: (row: T) => T): void => {
    onChange(windows.map((w, i) => (i === idx ? mutator(w) : w)));
  };
  const removeAt = (idx: number): void => {
    onChange(windows.filter((_, i) => i !== idx));
  };
  const handleAdd = (): void => {
    onChange([...windows, createEmpty()]);
  };

  return (
    <div className={styles.wrap}>
      {windows.length === 0 && (
        <p className={styles.empty}>{emptyStateText ?? t('timeWindows.emptyStateDefault')}</p>
      )}

      {windows.length > 0 && (
        <div className={styles.list}>
          {windows.map((w, i) => {
            const days = getDays(w);
            const noDays = days.length === 0;
            return (
              <div key={getKey ? getKey(w, i) : i} className={styles.row}>
                <div className={styles.dayChips}>
                  {DAY_KEYS.map((dayKey, dow) => {
                    const active = days.includes(dow);
                    return (
                      <button
                        type="button"
                        key={dow}
                        className={active ? styles.dayChipActive : styles.dayChip}
                        aria-pressed={active}
                        disabled={disabled}
                        // Derived from `row` — the CURRENT element handed
                        // in by updateAt — not from the `days`/`active`
                        // captured when this handler was created. The
                        // captured form was not a function of its own
                        // input: two chip toggles landing inside one
                        // commit boundary both computed from the same
                        // stale day-set, so the second overwrote the
                        // first with an array built from a state that no
                        // longer existed. Mon then Tue yielded [Tue].
                        //
                        // React 19 flushes clicks at discrete priority,
                        // so ordinary taps commit separately and this was
                        // latent rather than reproducible — but a <select>
                        // change and a click arriving in the same task,
                        // or any future startTransition around this
                        // subtree, opens it.
                        onClick={() => updateAt(i, (row) => {
                          const cur = getDays(row);
                          return setDays(
                            row,
                            cur.includes(dow)
                              ? cur.filter((y) => y !== dow)
                              : [...cur, dow].sort((a, b) => a - b),
                          );
                        })}
                      >
                        {t(`timeWindows.days.${dayKey}`)}
                      </button>
                    );
                  })}
                </div>

                <div className={styles.fieldGrid}>
                  <label className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>{t('timeWindows.start')}</span>
                    {/* 24-entry select per the founder spec — single
                        tap, no separate AM/PM toggle state to keep
                        in sync. Value is the underlying 0-23 int the
                        schema stores; only the rendered label shifts
                        to 12-hour AM/PM via formatHour12. clampHour
                        retained on the change handler as a defensive
                        backstop against malformed events. */}
                    <select
                      className={formStyles.input}
                      value={getStartHour(w)}
                      onChange={(e) => updateAt(i, (row) => setStartHour(
                        row,
                        clampHour(parseInt(e.target.value, 10), startHourMin, startHourMax),
                      ))}
                      disabled={disabled}
                    >
                      {hourOptions(startHourMin, startHourMax).map((h) => (
                        <option key={h} value={h}>{formatHour12(h)}</option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>{t('timeWindows.end')}</span>
                    {/* End-hour range is 1-24 inclusive — 24 is the
                        "until midnight" sentinel per
                        Charger.guest_time_windows docblock. formatHour12
                        renders 24 as "12:00 AM" (same label as 0);
                        the day picker carries the calendar-day context
                        so the driver reads "Mon · 6:00 PM → 12:00 AM"
                        as "Monday evening through midnight." */}
                    <select
                      className={formStyles.input}
                      value={getEndHour(w)}
                      onChange={(e) => updateAt(i, (row) => setEndHour(
                        row,
                        clampHour(parseInt(e.target.value, 10), endHourMin, endHourMax),
                      ))}
                      disabled={disabled}
                    >
                      {hourOptions(endHourMin, endHourMax).map((h) => (
                        <option key={h} value={h}>{formatHour12(h)}</option>
                      ))}
                    </select>
                  </label>
                  {extraColumn && (
                    <label className={styles.fieldGroup}>
                      <span className={styles.fieldLabel}>{extraColumn.label}</span>
                      {extraColumn.render(
                        w,
                        (next) => updateAt(i, () => next),
                        disabled,
                      )}
                    </label>
                  )}
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => removeAt(i)}
                    aria-label={t('timeWindows.removeWindow')}
                    disabled={disabled || (!allowEmpty && windows.length <= 1)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                {noDays && (
                  <p className={styles.warning}>
                    {t('timeWindows.noDaysWarning')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className={styles.addBtn}
        onClick={handleAdd}
        disabled={disabled}
      >
        <Plus size={13} /> {addButtonText ?? t('timeWindows.addButtonDefault')}
      </button>
    </div>
  );
}
